import { FormEvent, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, HelpCircle, Loader2, User, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/cn";
import { paths } from "@/routes/path";
import { admissionsService } from "@/services/admissionsService";
import { sanitizeInput } from "@/utils/security";
import { ApplicationStatus } from "@/models/application.model";
import { AdmissionStatusResult } from "@/types/admissionStatus";
import { AdmissionStatusResponseJSON } from "@/types/admissions";

const variantStyles = {
  info: "border-[#827C00] bg-[#B7AE0014] text-[#827C00]",
  success: "border-[#BBF7D0] bg-[#F0FDF4] text-[#166534]",
  warning: "border-[#FACC15] bg-[#FEF9C3] text-[#92400E]",
  error: "border-[#FECACA] bg-[#FEF2F2] text-[#B91C1C]",
} as const;

const formatProgrammeLabel = (identifier?: string | null): string | null => {
  if (!identifier) {
    return null;
  }

  return identifier
    .split(/[-_]/)
    .filter((segment) => segment.trim().length > 0)
    .map((segment) => {
      const lower = segment.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
};

const buildApplyUrl = (status?: AdmissionStatusResponseJSON["data"]) => {
  const params = new URLSearchParams();

  if (status?.programId?.trim()) {
    params.set("programmeId", status.programId.trim());
  }
  if (status?.programmeTitle?.trim()) {
    params.set("programmeName", status.programmeTitle.trim());
    params.set("programmeTitle", status.programmeTitle.trim());
  }
  if (status?.appId?.trim()) {
    params.set("appId", status.appId.trim());
  }
  if (status?.programDescription?.trim()) {
    params.set("instructions", status.programDescription.trim());
  }

  const query = params.toString();
  return query ? `${paths.public.apply.absolute}?${query}` : paths.public.apply.absolute;
};

const appendResumeLink = (
  message: string,
  status?: AdmissionStatusResponseJSON["data"],
) => {
  const trimmed = message.trim();
  const hasEndingPunctuation = /[.!?]$/.test(trimmed);
  const normalized = hasEndingPunctuation ? trimmed : `${trimmed}.`;
  const applyHref = buildApplyUrl(status);
  const resumeMessage = `Need to upload outstanding documents or finish your submission? <a href="${applyHref}" class="font-semibold text-[#7B2A40] underline">Resume your application</a> to continue where you stopped.`;
  return `${normalized}<br/><br/>${resumeMessage}`;
};

const parseProgrammeDurationValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    const parsed = trimmed ? Number(trimmed) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const normalizeDurationUnit = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

const formatDurationUnit = (
  duration: number | null | undefined,
  unit?: string | null,
): string => {
  const normalizedUnit = normalizeDurationUnit(unit);

  if (!normalizedUnit) {
    return duration === 1 ? "month" : "months";
  }

  const lower = normalizedUnit.toLowerCase();
  const singular = lower.endsWith("s") ? lower.slice(0, -1) : lower;

  if (duration === 1) {
    return singular || lower;
  }

  const plural = lower.endsWith("s") ? lower : `${singular || lower}s`;
  return plural || (duration === 1 ? "month" : "months");
};

export function AdmissionStatusPage() {
  const [uniqueId, setUniqueId] = useState("");
  const [status, setStatus] = useState<AdmissionStatusResult | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStatusLookup = useCallback(async (uniqueIdInput: string): Promise<AdmissionStatusResult> => {
    const sanitizedId = sanitizeInput(uniqueIdInput);

    if (sanitizedId.length < 6) {
      throw new Error("Please provide a valid admission unique ID.");
    }

    const statusResponse = await admissionsService.checkStatus(sanitizedId);

    if (!statusResponse) {
      return {
        appId: sanitizedId,
        applicantName: null,
        programmeId: null,
        programmeLabel: null,
        message:
          "We couldn't find an application with that ID. Please check and try again.",
        variant: "warning",
      };
    }

    const programmeDuration = parseProgrammeDurationValue(
      statusResponse.programDuration ?? (statusResponse as any)?.programmeDuration,
    );
    const programmeDurationUnit =
      normalizeDurationUnit(statusResponse.programDurationUnit) ??
      normalizeDurationUnit((statusResponse as any)?.program_duration_unit) ??
      normalizeDurationUnit((statusResponse as any)?.programmeDurationUnit);

    const baseDetails: Pick<
      AdmissionStatusResult,
      | "appId"
      | "applicantName"
      | "programmeId"
      | "programmeLabel"
      | "programmeTitle"
      | "programmeDescription"
      | "programmeDuration"
      | "programmeDurationUnit"
    > = {
      appId: statusResponse.appId ?? sanitizedId,
      applicantName: statusResponse.name ?? null,
      programmeId: statusResponse.programId ?? null,
      programmeLabel:
        statusResponse.programmeTitle ?? formatProgrammeLabel(statusResponse.programId),
      programmeTitle: statusResponse.programmeTitle ?? null,
      programmeDescription: statusResponse.programDescription ?? null,
      programmeDuration,
      programmeDurationUnit,
    };
    const feedback = statusResponse.adminNote?.trim() || null;

    switch (statusResponse.status) {
      case ApplicationStatus.ADMITTED:
        return {
          ...baseDetails,
          message: feedback || `Congratulations${statusResponse.name ? `, ${statusResponse.name}` : ""}! Your admission has been confirmed. 🎉`,
          variant: "success",
        };
      case ApplicationStatus.REJECTED:
        return {
          ...baseDetails,
          message: appendResumeLink(
            feedback ||
              "We're sorry, your application was not successful this cycle. Our team will contact you with next steps",
            statusResponse,
          ),
          variant: "error",
        };
      case ApplicationStatus.WITHDRAWN:
        return {
          ...baseDetails,
          message: `Your application has been withdrawn. ${feedback || "Please reach out to admissions if this was unexpected"}.`,
          variant: "warning",
        };
      case ApplicationStatus.PARTIAL_ADMIT:
        return {
          ...baseDetails,
          message: appendResumeLink(
            `You have been given a provisional admission. ${feedback || "Upload the specified documents to secure your spot"}`,
            statusResponse,
          ),
          variant: "info",
        };
      case ApplicationStatus.SUBMITTED:
      case ApplicationStatus.UNDER_REVIEW:
      default:
        return {
          ...baseDetails,
          message: `Your application is being reviewed.<br/>Feedback: ${feedback || "We will notify you once a decision is ready"}.`,
          variant: "info",
        };
    }
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmedId = uniqueId.trim();

      if (!trimmedId) {
        setStatus(null);
        setError("Please enter your unique ID.");
        return;
      }

      try {
        setError(null);
        setIsChecking(true);
        const result = await handleStatusLookup(trimmedId);
        setStatus(result.message?.trim() ? result : null);
      } catch (err: any) {
        setStatus(null);
        setError(
          err?.message ?? "We couldn't retrieve your status right now. Try again.",
        );
      } finally {
        setIsChecking(false);
      }
    },
    [handleStatusLookup, uniqueId],
  );

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-10">
      <div className="space-y-4 text-center">
        <div className="flex justify-start">
          <Link
            to={paths.auth.login.absolute}
            className="inline-flex items-center gap-2 text-sm font-medium text-[#7B2A40] underline-offset-4 hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to login
          </Link>
        </div>
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#98A2B3]">
            Admissions
          </p>
          <h1 className="text-3xl font-bold text-[#1F2937]">
            Check your admission status
          </h1>
          <p className="text-sm text-[#475467]">
            Enter the Application ID you received in your email to see your application status.
          </p>
        </div>
      </div>

      <div className="rounded-[32px] border border-[#F2F4F7] bg-white p-8 shadow-[0_20px_40px_rgba(15,23,42,0.08)]">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="admission-unique-id"
              className="text-sm font-medium text-[#4E1D31]"
            >
              Application ID
            </label>
            <Input
              id="admission-unique-id"
              value={uniqueId}
              onChange={(event) => setUniqueId(event.target.value)}
              placeholder="************"
              rightIcon={
                <span className="pointer-events-none text-[#98A2B3]">
                  <HelpCircle className="h-4 w-4" />
                </span>
              }
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={isChecking}
            className="h-11 w-full rounded-xl bg-[#7B2A40] text-sm font-semibold text-white transition hover:bg-[#651F34] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isChecking ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                Checking...
              </span>
            ) : (
              "Check status"
            )}
          </Button>
        </form>

        <div className="mt-8 space-y-3">
          <div className="rounded-2xl bg-[#F7F8FD] px-4 py-3 text-center text-sm text-[#98A2B3]">
            {status
              ? "Here's the current update for your application."
              : "No status yet. Enter your Application ID to see an update."}
          </div>

          {status ? (
            <div className="space-y-4">
              <div
                className={cn(
                  "rounded-xl border-2 border-dashed px-4 py-3 text-sm text-center",
                  variantStyles[status.variant ?? "info"],
                )}
                dangerouslySetInnerHTML={{ __html: status.message }}
              />

              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-left shadow-sm">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F8E8ED] text-[#7B2A40]">
                      <User className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.25em] text-[#98A2B3]">
                        Applicant
                      </p>
                      <p className="text-sm font-semibold text-[#1F2937]">
                        {status.applicantName?.trim() || "Not provided"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E8F1FD] text-[#1D4ED8]">
                      <GraduationCap className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.25em] text-[#98A2B3]">
                        Programme
                      </p>
                      <p className="text-sm font-semibold text-[#1F2937]">
                        {status.programmeTitle?.trim() ||
                          status.programmeLabel?.trim() ||
                          status.programmeId?.trim() ||
                          "Not provided"}
                      </p>
                      {status.programmeDescription?.trim() ? (
                        <p className="mt-1 text-xs text-[#6B7280]">
                          {status.programmeDescription?.trim()}
                        </p>
                      ) : null}
                      {typeof status.programmeDuration === "number" ? (
                        <p className="mt-2 text-xs font-medium text-[#111827]">
                          Duration: {status.programmeDuration}{" "}
                          {formatDurationUnit(
                            status.programmeDuration,
                            status.programmeDurationUnit,
                          )}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <p className="mt-8 text-xs text-[#667085]">
          Disclaimer: If the application is rejected, your money will be refunded back to
          your account.
        </p>

        <div className="mt-4 flex justify-end text-sm text-[#475467]">
          <span className="mr-1">Have any issues?</span>
          <a href="mailto:support@kadbs.edu" className="text-[#111827] underline">
            Contact support
          </a>
        </div>
      </div>
    </div>
  );
}
